import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft,
  RefreshCw,
  FileText,
  Image,
  FileSpreadsheet,
  Presentation,
  Globe,
  Merge,
  Split,
  RotateCcw,
  Trash2,
  Copy,
  Scissors,
  Minimize2,
  Droplet,
  Hash,
  EyeOff,
  Lock,
  Unlock,
  PenTool,
  Layers,
  Bot,
  MessageSquare,
  Languages,
  HelpCircle,
  ScanText,
  FormInput,
  Info,
  Download,
  Upload,
  CheckCircle2,
  FileType,
  Type,
  Highlighter,
  Stamp,
  FolderOpen,
  Code
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppChat from "@/components/WhatsAppChat";
import RelatedTools from "@/components/RelatedTools";
import PDFEditorCanvas from "@/components/premium/PDFEditorCanvas";
import AIProgressLoader from "@/components/premium/AIProgressLoader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/pdf-tools`;

export default function PDFTools() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTool, setActiveTool] = useState("jpg-to-pdf");
  const [activeCategory, setActiveCategory] = useState("convert-to-pdf");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Get tool from URL params
  useEffect(() => {
    const toolFromUrl = searchParams.get('tool');
    if (toolFromUrl) {
      setActiveTool(toolFromUrl);
    }
  }, [searchParams]);

  // Form states
  const [quality, setQuality] = useState("medium");
  const [rotation, setRotation] = useState("90");
  const [watermarkText, setWatermarkText] = useState("");
  const [watermarkPosition, setWatermarkPosition] = useState("center");
  const [password, setPassword] = useState("");
  const [pageNumbers, setPageNumbers] = useState("");
  const [numberPosition, setNumberPosition] = useState("bottom-center");
  const [redactText, setRedactText] = useState("");
  const [aiQuery, setAiQuery] = useState("");
  const [targetLanguage, setTargetLanguage] = useState("Hindi");
  
  // New states for edit tools
  const [textToAdd, setTextToAdd] = useState("");
  const [textX, setTextX] = useState(100);
  const [textY, setTextY] = useState(500);
  const [textPage, setTextPage] = useState(1);
  const [textColor, setTextColor] = useState("black");
  const [textFontSize, setTextFontSize] = useState(12);
  const [highlightX, setHighlightX] = useState(100);
  const [highlightY, setHighlightY] = useState(500);
  const [highlightW, setHighlightW] = useState(200);
  const [highlightH, setHighlightH] = useState(20);
  const [highlightColor, setHighlightColor] = useState("yellow");
  const [stampType, setStampType] = useState("APPROVED");
  const [stampPosition, setStampPosition] = useState("center");
  const [bulkOperation, setBulkOperation] = useState("compress");
  const [textContent, setTextContent] = useState("");
  const [htmlContent, setHtmlContent] = useState("");

  const toolCategories = [
    {
      id: "convert-to-pdf",
      name: "Convert to PDF",
      tools: [
        { id: "jpg-to-pdf", name: "JPG to PDF", icon: <Image className="w-4 h-4" />, color: "bg-yellow-500" },
        { id: "word-to-pdf", name: "Word to PDF", icon: <FileText className="w-4 h-4" />, color: "bg-blue-500" },
        { id: "powerpoint-to-pdf", name: "PPT to PDF", icon: <Presentation className="w-4 h-4" />, color: "bg-orange-500" },
        { id: "excel-to-pdf", name: "Excel to PDF", icon: <FileSpreadsheet className="w-4 h-4" />, color: "bg-green-500" }
      ]
    },
    {
      id: "convert-from-pdf",
      name: "Convert from PDF",
      tools: [
        { id: "pdf-to-jpg", name: "PDF to JPG", icon: <Image className="w-4 h-4" />, color: "bg-yellow-500" },
        { id: "pdf-to-word", name: "PDF to Word", icon: <FileText className="w-4 h-4" />, color: "bg-blue-500" },
        { id: "pdf-to-powerpoint", name: "PDF to PPT", icon: <Presentation className="w-4 h-4" />, color: "bg-orange-500" },
        { id: "ppt-to-pdf", name: "PPT to PDF", icon: <Presentation className="w-4 h-4" />, color: "bg-blue-500" },
        { id: "pdf-to-excel", name: "PDF to Excel", icon: <FileSpreadsheet className="w-4 h-4" />, color: "bg-green-500" },
        { id: "pdf-to-pdfa", name: "PDF to PDF/A", icon: <FileType className="w-4 h-4" />, color: "bg-indigo-500" }
      ]
    },
    {
      id: "organize",
      name: "Organize",
      tools: [
        { id: "merge", name: "Merge PDF", icon: <Merge className="w-4 h-4" />, color: "bg-blue-600" },
        { id: "split", name: "Split PDF", icon: <Split className="w-4 h-4" />, color: "bg-purple-600" },
        { id: "rotate", name: "Rotate PDF", icon: <RotateCcw className="w-4 h-4" />, color: "bg-cyan-600" },
        { id: "delete-pages", name: "Delete Pages", icon: <Trash2 className="w-4 h-4" />, color: "bg-red-600" },
        { id: "extract-pages", name: "Extract Pages", icon: <Copy className="w-4 h-4" />, color: "bg-green-600" },
        { id: "reorder", name: "Reorder Pages", icon: <Layers className="w-4 h-4" />, color: "bg-amber-600" },
        { id: "edit-pdf", name: "Edit PDF (Canvas)", icon: <Layers className="w-4 h-4" />, color: "bg-purple-600" }
      ]
    },
    {
      id: "edit",
      name: "View & Edit",
      tools: [
        { id: "compress", name: "Compress PDF", icon: <Minimize2 className="w-4 h-4" />, color: "bg-red-500" },
        { id: "crop", name: "Crop PDF", icon: <Scissors className="w-4 h-4" />, color: "bg-teal-500" },
        { id: "watermark", name: "Watermark", icon: <Droplet className="w-4 h-4" />, color: "bg-blue-400" },
        { id: "number-pages", name: "Number Pages", icon: <Hash className="w-4 h-4" />, color: "bg-violet-500" },
        { id: "add-text", name: "Add Text", icon: <Type className="w-4 h-4" />, color: "bg-blue-600" },
        { id: "highlight", name: "Highlight", icon: <Highlighter className="w-4 h-4" />, color: "bg-yellow-500" },
        { id: "stamp", name: "Add Stamp", icon: <Stamp className="w-4 h-4" />, color: "bg-purple-500" },
        { id: "redact", name: "Redact PDF", icon: <EyeOff className="w-4 h-4" />, color: "bg-gray-700" }
      ]
    },
    {
      id: "security",
      name: "Sign & Security",
      tools: [
        { id: "protect", name: "Protect PDF", icon: <Lock className="w-4 h-4" />, color: "bg-red-600" },
        { id: "unlock", name: "Unlock PDF", icon: <Unlock className="w-4 h-4" />, color: "bg-green-600" },
        { id: "sign", name: "Sign PDF", icon: <PenTool className="w-4 h-4" />, color: "bg-amber-600" },
        { id: "flatten", name: "Flatten PDF", icon: <Layers className="w-4 h-4" />, color: "bg-purple-600" }
      ]
    },
    {
      id: "ai",
      name: "AI PDF",
      tools: [
        { id: "ai-summarize", name: "AI Summarizer", icon: <Bot className="w-4 h-4" />, color: "bg-gradient-to-r from-blue-500 to-purple-500" },
        { id: "ai-chat", name: "Chat with PDF", icon: <MessageSquare className="w-4 h-4" />, color: "bg-gradient-to-r from-green-500 to-teal-500" },
        { id: "ai-translate", name: "Translate PDF", icon: <Languages className="w-4 h-4" />, color: "bg-gradient-to-r from-orange-500 to-red-500" },
        { id: "ai-questions", name: "AI Questions", icon: <HelpCircle className="w-4 h-4" />, color: "bg-gradient-to-r from-pink-500 to-purple-500" }
      ]
    },
    {
      id: "png-tools",
      name: "PNG Tools",
      tools: [
        { id: "png-to-pdf", name: "PNG to PDF", icon: <Image className="w-4 h-4" />, color: "bg-slate-100 text-slate-700" },
        { id: "pdf-to-png", name: "PDF to PNG", icon: <Image className="w-4 h-4" />, color: "bg-slate-100 text-slate-700" },
        { id: "jpg-to-png", name: "JPG to PNG", icon: <Image className="w-4 h-4" />, color: "bg-slate-100 text-slate-700" },
        { id: "png-to-jpg", name: "PNG to JPG", icon: <Image className="w-4 h-4" />, color: "bg-slate-100 text-slate-700" },
        { id: "png-compress", name: "Compress PNG", icon: <Minimize2 className="w-4 h-4" />, color: "bg-slate-100 text-slate-700" },
        { id: "png-resize", name: "Resize PNG", icon: <Scissors className="w-4 h-4" />, color: "bg-slate-100 text-slate-700" }
      ]
    },
    {
      id: "more",
      name: "More Tools",
      tools: [
        { id: "ocr", name: "PDF OCR", icon: <ScanText className="w-4 h-4" />, color: "bg-indigo-600" },
        { id: "form-fill", name: "Form Filler", icon: <FormInput className="w-4 h-4" />, color: "bg-cyan-600" },
        { id: "pdf-info", name: "PDF Info", icon: <Info className="w-4 h-4" />, color: "bg-gray-600" },
        { id: "bulk-process", name: "Bulk Process", icon: <FolderOpen className="w-4 h-4" />, color: "bg-orange-600" },
        { id: "text-to-pdf", name: "Text to PDF", icon: <FileText className="w-4 h-4" />, color: "bg-green-600" },
        { id: "html-to-pdf", name: "HTML to PDF", icon: <Code className="w-4 h-4" />, color: "bg-red-600" }
      ]
    }
  ];

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedFile({
          name: file.name,
          size: file.size,
          data: reader.result.split(',')[1] // Base64 data
        });
      };
      reader.readAsDataURL(file);
      toast.success(`File uploaded: ${file.name}`);
    }
  };

  const handleMultipleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const filePromises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            name: file.name,
            size: file.size,
            data: reader.result.split(',')[1]
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(filePromises).then(fileData => {
      setUploadedFiles(fileData);
      toast.success(`${files.length} files uploaded`);
    });
  };

  // Endpoint mapping - tool ID to actual API endpoint
  const endpointMap = {
    // Convert to PDF
    "jpg-to-pdf": "/convert/image-to-pdf",
    "png-to-pdf": "/convert/image-to-pdf",
    "word-to-pdf": "/convert/word-to-pdf",
    "powerpoint-to-pdf": "/convert/word-to-pdf",
    "excel-to-pdf": "/convert/word-to-pdf",
    "text-to-pdf": "/convert/text-to-pdf",
    "html-to-pdf": "/convert/html-to-pdf",
    // PNG Tools
    "jpg-to-png": "/convert/image-to-image",
    "png-to-jpg": "/convert/image-to-image",
    "png-compress": "/convert/image-compress",
    "png-resize": "/convert/image-resize",
    // Convert from PDF
    "pdf-to-jpg": "/convert/pdf-to-image",
    "pdf-to-png": "/convert/pdf-to-image",
    "pdf-to-word": "/convert/pdf-to-word",             // real DOCX via pdf2docx
    "pdf-to-powerpoint": "/convert/pdf-to-image",
    "pdf-to-excel": "/convert/pdf-to-image",
    "pdf-to-pdfa": "/convert/pdf-to-image",
    "ppt-to-pdf": "/convert/ppt-to-pdf",               // NEW: real PPT→PDF
    // Organize
    "merge": "/organize/merge",
    "split": "/organize/split",
    "rotate": "/organize/rotate",
    "delete-pages": "/organize/delete-pages",
    "extract-pages": "/organize/extract",
    "reorder": "/organize/reorder",
    "edit-pdf": "__canvas__",                          // handled by <PDFEditorCanvas />
    // Edit
    "compress": "/optimize/compress-hq",               // upgraded to PyMuPDF for better ratio
    "crop": "/edit/crop",
    "watermark": "/edit/watermark",
    "number-pages": "/edit/page-numbers",
    "add-text": "/edit/add-text",
    "highlight": "/edit/highlight",
    "stamp": "/edit/stamp",
    "redact": "/edit/redact",
    // Security
    "protect": "/security/protect",
    "unlock": "/security/unlock",
    "flatten": "/security/flatten",
    // AI
    "ai-summarize": "/ai/summarize",
    "ai-chat": "/ai/chat",
    // More
    "ocr": "/ocr/extract-text",
    "pdf-info": "/info"
  };

  const processFile = async (endpoint, additionalData = {}) => {
    if (!uploadedFile && !uploadedFiles.length) {
      toast.error("Please upload a file first");
      return;
    }

    setLoading(true);
    try {
      // Use mapped endpoint or provided endpoint
      const actualEndpoint = endpointMap[activeTool] || endpoint;
      const response = await axios.post(`${API}${actualEndpoint}`, {
        file_data: uploadedFile?.data || "",
        filename: uploadedFile?.name || "document.pdf",
        ...additionalData
      });
      // Clean result to avoid rendering objects as children
      const cleanResult = { ...response.data };
      if (cleanResult.details && typeof cleanResult.details === 'object') {
        Object.keys(cleanResult.details).forEach(key => {
          if (typeof cleanResult.details[key] === 'object') {
            cleanResult.details[key] = JSON.stringify(cleanResult.details[key]);
          }
        });
      }
      setResult(cleanResult);
      toast.success(response.data.message || "Operation completed!");
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentTool = () => {
    for (const category of toolCategories) {
      const tool = category.tools.find(t => t.id === activeTool);
      if (tool) return { ...tool, category: category.name };
    }
    return null;
  };

  const renderToolUI = () => {
    const tool = getCurrentTool();
    if (!tool) return null;

    // Canvas editor branch — full preview + reorder + rotate + delete + text
    if (activeTool === "edit-pdf") {
      return (
        <div>
          {!uploadedFile ? (
            <div className="border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center hover:border-[#A855F7]/40 transition-colors">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                className="hidden"
                id="edit-pdf-upload"
                data-testid="edit-pdf-upload"
              />
              <label htmlFor="edit-pdf-upload" className="cursor-pointer">
                <Upload className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                <p className="text-base font-semibold text-[#111111]">Upload a PDF to edit</p>
                <p className="text-sm text-slate-500 mt-1">You&rsquo;ll see thumbnails for every page and can reorder / rotate / delete / add text.</p>
              </label>
            </div>
          ) : (
            <PDFEditorCanvas
              file={uploadedFile}
              onApplied={(res) => setResult({ ...res, success: true, operation: "edit" })}
            />
          )}
        </div>
      );
    }

    // Common file upload component
    const FileUploader = ({ multiple = false, accept = ".pdf" }) => (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-400 transition-colors">
        <input
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={multiple ? handleMultipleFileUpload : handleFileUpload}
          className="hidden"
          id="file-upload"
          data-testid="file-upload"
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-700">
            {multiple ? "Drop files here or click to upload" : "Drop file here or click to upload"}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {accept === ".pdf" ? "PDF files only" : "Supported formats: " + accept}
          </p>
        </label>
        {uploadedFile && !multiple && (
          <div className="mt-4 p-3 bg-green-50 rounded-lg flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-green-700">{uploadedFile.name}</span>
            <span className="text-gray-500 text-sm">({(uploadedFile.size / 1024).toFixed(1)} KB)</span>
          </div>
        )}
        {uploadedFiles.length > 0 && multiple && (
          <div className="mt-4 space-y-2">
            {uploadedFiles.map((f, i) => (
              <div key={i} className="p-2 bg-green-50 rounded flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700">{f.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );

    // ALL Convert tools - uses endpointMap for correct API calls
    if (activeTool.includes("-to-")) {
      const isConvertToPdf = activeTool.endsWith("-to-pdf");
      const acceptTypes = isConvertToPdf ? 
        (activeTool === "jpg-to-pdf" || activeTool === "png-to-pdf" ? ".jpg,.jpeg,.png" :
         activeTool === "word-to-pdf" ? ".doc,.docx" :
         activeTool === "powerpoint-to-pdf" ? ".ppt,.pptx" :
         activeTool === "ppt-to-pdf" ? ".ppt,.pptx" :
         activeTool === "excel-to-pdf" ? ".xls,.xlsx" :
         activeTool === "html-to-pdf" ? ".html,.htm" :
         activeTool === "text-to-pdf" ? ".txt" : ".pdf") 
        : ".pdf";

      return (
        <div className="space-y-6">
          <FileUploader accept={acceptTypes} />
          <Button 
            onClick={() => {
              const endpoint = endpointMap[activeTool] || `/convert/${activeTool}`;
              processFile(endpoint, { output_format: isConvertToPdf ? "pdf" : "image" });
            }}
            disabled={loading || !uploadedFile}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            data-testid="convert-btn"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Convert {tool.name}
          </Button>
        </div>
      );
    }

    // Merge PDF
    if (activeTool === "merge") {
      return (
        <div className="space-y-6">
          <FileUploader multiple accept=".pdf" />
          <Button 
            onClick={async () => {
              if (uploadedFiles.length < 2) {
                toast.error("Please upload at least 2 PDF files");
                return;
              }
              setLoading(true);
              try {
                const response = await axios.post(`${API}/organize/merge`, {
                  files: uploadedFiles.map(f => f.data),
                  filenames: uploadedFiles.map(f => f.name)
                });
                setResult(response.data);
                toast.success("PDFs merged successfully!");
              } catch (error) {
                toast.error("Merge failed");
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading || uploadedFiles.length < 2}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Merge className="w-4 h-4 mr-2" />}
            Merge PDFs
          </Button>
        </div>
      );
    }

    // Split PDF
    if (activeTool === "split") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <div>
            <label className="block text-sm font-medium mb-2">Pages to extract (e.g., 1-3, 5, 7-10)</label>
            <Input 
              placeholder="Leave empty to split all pages" 
              value={pageNumbers}
              onChange={(e) => setPageNumbers(e.target.value)}
            />
          </div>
          <Button 
            onClick={() => processFile("/organize/split", { split_type: "all", pages: pageNumbers })}
            disabled={loading || !uploadedFile}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Split className="w-4 h-4 mr-2" />}
            Split PDF
          </Button>
        </div>
      );
    }

    // Rotate PDF
    if (activeTool === "rotate") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <div>
            <label className="block text-sm font-medium mb-2">Rotation</label>
            <Select value={rotation} onValueChange={setRotation}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="90">90° Clockwise</SelectItem>
                <SelectItem value="180">180°</SelectItem>
                <SelectItem value="270">90° Counter-clockwise</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={() => processFile("/organize/rotate", { rotation: parseInt(rotation), pages: "all" })}
            disabled={loading || !uploadedFile}
            className="w-full bg-cyan-600 hover:bg-cyan-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
            Rotate PDF
          </Button>
        </div>
      );
    }

    // Compress PDF
    if (activeTool === "compress") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <div>
            <label className="block text-sm font-medium mb-2">Compression Quality</label>
            <Select value={quality} onValueChange={setQuality}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low (Smallest file, lower quality)</SelectItem>
                <SelectItem value="medium">Medium (Balanced)</SelectItem>
                <SelectItem value="high">High (Best quality, larger file)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => processFile("/optimize/compress-hq", { level: quality })}
            disabled={loading || !uploadedFile}
            className="w-full bg-[#111111] hover:bg-black text-white rounded-full"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Minimize2 className="w-4 h-4 mr-2" />}
            Compress PDF
          </Button>
        </div>
      );
    }

    // Watermark
    if (activeTool === "watermark") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <div>
            <label className="block text-sm font-medium mb-2">Watermark Text</label>
            <Input 
              placeholder="CONFIDENTIAL" 
              value={watermarkText}
              onChange={(e) => setWatermarkText(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Position</label>
            <Select value={watermarkPosition} onValueChange={setWatermarkPosition}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="diagonal">Diagonal</SelectItem>
                <SelectItem value="top">Top</SelectItem>
                <SelectItem value="bottom">Bottom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={() => processFile("/edit/watermark", { watermark_text: watermarkText, position: watermarkPosition, opacity: 0.5 })}
            disabled={loading || !uploadedFile || !watermarkText}
            className="w-full bg-blue-500 hover:bg-blue-600"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Droplet className="w-4 h-4 mr-2" />}
            Add Watermark
          </Button>
        </div>
      );
    }

    // Protect PDF
    if (activeTool === "protect") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <div>
            <label className="block text-sm font-medium mb-2">Password</label>
            <Input 
              type="password"
              placeholder="Enter password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <Button 
            onClick={() => processFile("/security/protect", { password })}
            disabled={loading || !uploadedFile || !password}
            className="w-full bg-red-600 hover:bg-red-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
            Protect PDF
          </Button>
        </div>
      );
    }

    // Unlock PDF
    if (activeTool === "unlock") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <div>
            <label className="block text-sm font-medium mb-2">Current Password</label>
            <Input 
              type="password"
              placeholder="Enter current password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <Button 
            onClick={() => processFile("/security/unlock", { password })}
            disabled={loading || !uploadedFile || !password}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Unlock className="w-4 h-4 mr-2" />}
            Unlock PDF
          </Button>
        </div>
      );
    }

    // Number Pages
    if (activeTool === "number-pages") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <div>
            <label className="block text-sm font-medium mb-2">Position</label>
            <Select value={numberPosition} onValueChange={setNumberPosition}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bottom-center">Bottom Center</SelectItem>
                <SelectItem value="bottom-right">Bottom Right</SelectItem>
                <SelectItem value="top-center">Top Center</SelectItem>
                <SelectItem value="top-right">Top Right</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={() => processFile("/edit/number-pages", { position: numberPosition, start_number: 1, format: "1" })}
            disabled={loading || !uploadedFile}
            className="w-full bg-violet-600 hover:bg-violet-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Hash className="w-4 h-4 mr-2" />}
            Add Page Numbers
          </Button>
        </div>
      );
    }

    // AI Summarize
    if (activeTool === "ai-summarize") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <Button 
            onClick={() => processFile("/ai/summarize", { action: "summarize" })}
            disabled={loading || !uploadedFile}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
            Summarize PDF with AI
          </Button>
        </div>
      );
    }

    // AI Chat
    if (activeTool === "ai-chat") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <div>
            <label className="block text-sm font-medium mb-2">Ask a question about the PDF</label>
            <Textarea 
              placeholder="What is the main topic of this document?"
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              rows={3}
            />
          </div>
          <Button 
            onClick={() => processFile("/ai/chat", { action: "chat", query: aiQuery })}
            disabled={loading || !uploadedFile || !aiQuery}
            className="w-full bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
            Ask AI
          </Button>
        </div>
      );
    }

    // AI Translate
    if (activeTool === "ai-translate") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <div>
            <label className="block text-sm font-medium mb-2">Target Language</label>
            <Select value={targetLanguage} onValueChange={setTargetLanguage}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Hindi">Hindi</SelectItem>
                <SelectItem value="Spanish">Spanish</SelectItem>
                <SelectItem value="French">French</SelectItem>
                <SelectItem value="German">German</SelectItem>
                <SelectItem value="Chinese">Chinese</SelectItem>
                <SelectItem value="Japanese">Japanese</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={() => processFile("/ai/translate", { action: "translate", query: targetLanguage })}
            disabled={loading || !uploadedFile}
            className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Languages className="w-4 h-4 mr-2" />}
            Translate PDF
          </Button>
        </div>
      );
    }

    // AI Questions
    if (activeTool === "ai-questions") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <Button 
            onClick={() => processFile("/ai/generate-questions", { action: "questions" })}
            disabled={loading || !uploadedFile}
            className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <HelpCircle className="w-4 h-4 mr-2" />}
            Generate Questions
          </Button>
        </div>
      );
    }

    // ADD TEXT TO PDF
    if (activeTool === "add-text") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <div>
            <label className="block text-sm font-medium mb-2">Text to Add</label>
            <Input 
              placeholder="Enter text to add to PDF" 
              value={textToAdd}
              onChange={(e) => setTextToAdd(e.target.value)}
              data-testid="add-text-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">X Position</label>
              <Input 
                type="number" 
                value={textX}
                onChange={(e) => setTextX(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Y Position</label>
              <Input 
                type="number" 
                value={textY}
                onChange={(e) => setTextY(Number(e.target.value))}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Page</label>
              <Input 
                type="number" 
                min="1"
                value={textPage}
                onChange={(e) => setTextPage(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Font Size</label>
              <Input 
                type="number" 
                value={textFontSize}
                onChange={(e) => setTextFontSize(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Color</label>
              <Select value={textColor} onValueChange={setTextColor}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="black">Black</SelectItem>
                  <SelectItem value="red">Red</SelectItem>
                  <SelectItem value="blue">Blue</SelectItem>
                  <SelectItem value="green">Green</SelectItem>
                  <SelectItem value="gray">Gray</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button 
            onClick={() => processFile("/edit/add-text", { 
              text_to_add: textToAdd,
              x: textX,
              y: textY,
              page: textPage,
              font_size: textFontSize,
              color: textColor
            })}
            disabled={loading || !uploadedFile || !textToAdd}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Type className="w-4 h-4 mr-2" />}
            Add Text to PDF
          </Button>
        </div>
      );
    }

    // HIGHLIGHT
    if (activeTool === "highlight") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">X Position</label>
              <Input 
                type="number" 
                value={highlightX}
                onChange={(e) => setHighlightX(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Y Position</label>
              <Input 
                type="number" 
                value={highlightY}
                onChange={(e) => setHighlightY(Number(e.target.value))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Width</label>
              <Input 
                type="number" 
                value={highlightW}
                onChange={(e) => setHighlightW(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Height</label>
              <Input 
                type="number" 
                value={highlightH}
                onChange={(e) => setHighlightH(Number(e.target.value))}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Highlight Color</label>
            <Select value={highlightColor} onValueChange={setHighlightColor}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yellow">Yellow</SelectItem>
                <SelectItem value="green">Green</SelectItem>
                <SelectItem value="blue">Blue</SelectItem>
                <SelectItem value="pink">Pink</SelectItem>
                <SelectItem value="orange">Orange</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={() => processFile("/edit/highlight", { 
              x: highlightX,
              y: highlightY,
              width: highlightW,
              height: highlightH,
              page: textPage,
              color: highlightColor,
              opacity: 0.3
            })}
            disabled={loading || !uploadedFile}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Highlighter className="w-4 h-4 mr-2" />}
            Add Highlight
          </Button>
        </div>
      );
    }

    // STAMP
    if (activeTool === "stamp") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <div>
            <label className="block text-sm font-medium mb-2">Stamp Type</label>
            <Select value={stampType} onValueChange={setStampType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="APPROVED">APPROVED</SelectItem>
                <SelectItem value="DRAFT">DRAFT</SelectItem>
                <SelectItem value="CONFIDENTIAL">CONFIDENTIAL</SelectItem>
                <SelectItem value="REVIEWED">REVIEWED</SelectItem>
                <SelectItem value="COPY">COPY</SelectItem>
                <SelectItem value="FINAL">FINAL</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Position</label>
            <Select value={stampPosition} onValueChange={setStampPosition}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="center">Center (Diagonal)</SelectItem>
                <SelectItem value="top-right">Top Right</SelectItem>
                <SelectItem value="top-left">Top Left</SelectItem>
                <SelectItem value="bottom-right">Bottom Right</SelectItem>
                <SelectItem value="bottom-left">Bottom Left</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Page Number</label>
            <Input 
              type="number" 
              min="1"
              value={textPage}
              onChange={(e) => setTextPage(Number(e.target.value))}
              placeholder="1"
            />
          </div>
          <Button 
            onClick={() => processFile("/edit/stamp", { 
              stamp_type: stampType,
              position: stampPosition,
              page: textPage
            })}
            disabled={loading || !uploadedFile}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Stamp className="w-4 h-4 mr-2" />}
            Add Stamp
          </Button>
        </div>
      );
    }

    // BULK PROCESSING
    if (activeTool === "bulk-process") {
      return (
        <div className="space-y-6">
          <div className="border-2 border-dashed border-violet-300 rounded-lg p-6 text-center bg-violet-50">
            <input
              type="file"
              accept=".pdf"
              multiple
              onChange={handleMultipleFileUpload}
              className="hidden"
              id="bulk-upload"
              data-testid="bulk-upload-input"
            />
            <label htmlFor="bulk-upload" className="cursor-pointer">
              <Upload className="w-12 h-12 mx-auto text-violet-400 mb-2" />
              <p className="font-medium text-violet-700">Drop multiple PDFs here or click to upload</p>
              <p className="text-sm text-violet-500">Up to 20 files</p>
            </label>
          </div>
          
          {uploadedFiles.length > 0 && (
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="font-medium text-green-700">{uploadedFiles.length} files ready</p>
              <ul className="text-sm text-green-600 mt-1">
                {uploadedFiles.map((f, i) => (
                  <li key={i}>{f.name}</li>
                ))}
              </ul>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium mb-2">Operation</label>
            <Select value={bulkOperation} onValueChange={setBulkOperation}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="compress">Compress All</SelectItem>
                <SelectItem value="rotate">Rotate All (90°)</SelectItem>
                <SelectItem value="add_page_numbers">Add Page Numbers</SelectItem>
                <SelectItem value="protect">Password Protect All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {bulkOperation === "protect" && (
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <Input 
                type="password"
                placeholder="Enter password for all PDFs" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          )}
          
          <Button 
            onClick={async () => {
              if (uploadedFiles.length === 0) {
                toast.error("Please upload files first");
                return;
              }
              setLoading(true);
              try {
                const response = await axios.post(`${API}/bulk/process`, {
                  files: uploadedFiles.map(f => f.data),
                  filenames: uploadedFiles.map(f => f.name),
                  operation: bulkOperation,
                  options: bulkOperation === "protect" ? { password } : {}
                });
                setResult(response.data);
                toast.success(response.data.message);
              } catch (error) {
                toast.error(error.response?.data?.detail || "Bulk processing failed");
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading || uploadedFiles.length === 0}
            className="w-full bg-orange-600 hover:bg-orange-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FolderOpen className="w-4 h-4 mr-2" />}
            Process {uploadedFiles.length} Files
          </Button>
        </div>
      );
    }

    // TEXT TO PDF
    if (activeTool === "text-to-pdf") {
      return (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Document Title</label>
            <Input 
              placeholder="My Document" 
              value={textToAdd}
              onChange={(e) => setTextToAdd(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Text Content</label>
            <Textarea 
              placeholder="Enter your text content here..."
              rows={10}
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              data-testid="text-content-input"
            />
          </div>
          <Button 
            onClick={async () => {
              if (!textContent) {
                toast.error("Please enter text content");
                return;
              }
              setLoading(true);
              try {
                const response = await axios.post(`${API}/convert/text-to-pdf`, {
                  text: textContent,
                  title: textToAdd || "Document",
                  font_size: 12,
                  page_size: "A4"
                });
                setResult(response.data);
                toast.success(response.data.message);
              } catch (error) {
                toast.error(error.response?.data?.detail || "Conversion failed");
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading || !textContent}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Convert Text to PDF
          </Button>
        </div>
      );
    }

    // HTML TO PDF
    if (activeTool === "html-to-pdf") {
      return (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">HTML Content</label>
            <Textarea 
              placeholder="<html><body><h1>Hello World</h1><p>Your content here...</p></body></html>"
              rows={10}
              value={htmlContent}
              onChange={(e) => setHtmlContent(e.target.value)}
              data-testid="html-content-input"
            />
          </div>
          <Button 
            onClick={async () => {
              if (!htmlContent) {
                toast.error("Please enter HTML content");
                return;
              }
              setLoading(true);
              try {
                const response = await axios.post(`${API}/convert/html-to-pdf`, {
                  html_content: htmlContent,
                  filename: "converted"
                });
                setResult(response.data);
                toast.success(response.data.message);
              } catch (error) {
                toast.error(error.response?.data?.detail || "Conversion failed");
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading || !htmlContent}
            className="w-full bg-red-600 hover:bg-red-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Code className="w-4 h-4 mr-2" />}
            Convert HTML to PDF
          </Button>
        </div>
      );
    }

    // OCR
    if (activeTool === "ocr") {
      return (
        <div className="space-y-6">
          <FileUploader />
          <Button 
            onClick={() => processFile("/ocr/extract-text", { language: "eng" })}
            disabled={loading || !uploadedFile}
            className="w-full bg-indigo-600 hover:bg-indigo-700"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ScanText className="w-4 h-4 mr-2" />}
            Extract Text (OCR)
          </Button>
        </div>
      );
    }

    // Default - simple file upload and process
    return (
      <div className="space-y-6">
        <FileUploader />
        {(activeTool === "delete-pages" || activeTool === "extract-pages") && (
          <div>
            <label className="block text-sm font-medium mb-2">Pages (e.g., 1,3,5-7)</label>
            <Input 
              placeholder="Enter page numbers" 
              value={pageNumbers}
              onChange={(e) => setPageNumbers(e.target.value)}
            />
          </div>
        )}
        <Button 
          onClick={() => {
            const endpoint = 
              activeTool === "delete-pages" ? "/organize/delete-pages" :
              activeTool === "extract-pages" ? "/organize/extract" :
              activeTool === "reorder" ? "/organize/reorder" :
              activeTool === "crop" ? "/edit/crop" :
              activeTool === "redact" ? "/edit/redact" :
              activeTool === "sign" ? "/security/sign" :
              activeTool === "flatten" ? "/security/flatten" :
              activeTool === "form-fill" ? "/forms/fill" :
              activeTool === "pdf-info" ? "/info" :
              activeTool === "pdf-to-jpg" ? "/convert/pdf-to-image" :
              activeTool === "pdf-to-word" ? "/edit/extract-text" :
              activeTool === "pdf-to-excel" ? "/edit/extract-text" :
              activeTool === "pdf-to-pdfa" ? "/info" :
              "/convert/image-to-pdf";
            processFile(endpoint, { pages: pageNumbers });
          }}
          disabled={loading || !uploadedFile}
          className="w-full"
        >
          {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : tool.icon}
          <span className="ml-2">Process {tool.name}</span>
        </Button>
      </div>
    );
  };

  const renderResult = () => {
    if (!result) return null;

    return (
      <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle2 className="w-6 h-6 text-green-600" />
          <span className="font-semibold text-green-800">{result.message || "Operation Successful!"}</span>
        </div>
        
        {result.summary && (
          <div className="mb-4 p-4 bg-white rounded-lg">
            <h4 className="font-medium mb-2">Summary:</h4>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{result.summary}</p>
          </div>
        )}

        {result.response && (
          <div className="mb-4 p-4 bg-white rounded-lg">
            <h4 className="font-medium mb-2">AI Response:</h4>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{result.response}</p>
          </div>
        )}

        {result.questions_generated && (
          <div className="mb-4 p-4 bg-white rounded-lg">
            <h4 className="font-medium mb-2">Generated Questions:</h4>
            <ul className="space-y-2">
              {result.questions_generated.map((q, i) => (
                <li key={i} className="text-sm p-2 bg-gray-50 rounded">
                  <span className="font-medium">{i + 1}.</span> {q.question}
                  <Badge className="ml-2" variant="outline">{q.type}</Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {result.text_extracted && (
          <div className="mb-4 p-4 bg-white rounded-lg">
            <h4 className="font-medium mb-2">Extracted Text:</h4>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{result.text_extracted}</p>
          </div>
        )}

        {result.details && Object.keys(result.details).length > 0 && (
          <div className="grid grid-cols-2 gap-2 text-sm">
            {Object.entries(result.details).map(([key, value]) => (
              <div key={key} className="flex justify-between p-2 bg-white rounded">
                <span className="text-gray-600">{key.replace(/_/g, ' ')}:</span>
                <span className="font-medium">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
              </div>
            ))}
          </div>
        )}

        {result.file_data && (
          <Button 
            className="mt-4 w-full" 
            variant="outline"
            onClick={() => {
              // Download the file
              const link = document.createElement('a');
              link.href = result.file_data;
              link.download = result.output_filename || 'processed.pdf';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              toast.success("Download started!");
            }}
          >
            <Download className="w-4 h-4 mr-2" />
            Download {result.output_filename || 'Result'}
          </Button>
        )}

        {result.output_files && result.output_files.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="font-medium text-sm">Download Files:</p>
            {result.output_files.map((file, idx) => (
              <Button 
                key={idx}
                variant="outline" 
                size="sm"
                className="mr-2 mb-2"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = file.file_data;
                  link.download = file.filename;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <Download className="w-3 h-3 mr-1" />
                {file.filename}
              </Button>
            ))}
          </div>
        )}

        {result.full_text && (
          <div className="mt-4">
            <p className="font-medium text-sm mb-2">Extracted Text:</p>
            <Textarea 
              value={result.full_text} 
              readOnly 
              className="min-h-[200px] font-mono text-sm"
            />
            <Button 
              variant="outline" 
              size="sm"
              className="mt-2"
              onClick={() => {
                navigator.clipboard.writeText(result.full_text);
                toast.success("Text copied to clipboard!");
              }}
            >
              <Copy className="w-3 h-3 mr-1" /> Copy Text
            </Button>
          </div>
        )}
      </div>
    );
  };

  const currentTool = getCurrentTool();

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans">
      <Header />

      {/* Premium Page Title */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6 w-full">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-[#111111] rounded-2xl flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-xs tracking-[0.18em] font-semibold text-[#A855F7] uppercase mb-1">Design Suite · PDF & PNG</p>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#111111]">PDF & PNG Tools</h1>
            <p className="text-slate-600 mt-1.5">50+ document tools — convert, edit, secure, and generate. All AI-enhanced.</p>
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-4 sticky top-24 max-h-[80vh] overflow-y-auto rounded-2xl border-slate-200 shadow-sm">
              {toolCategories.map((category) => (
                <div key={category.id} className="mb-6">
                  <h3 className="text-[10px] font-semibold text-[#A855F7] uppercase tracking-[0.15em] mb-2 px-1">
                    {category.name}
                  </h3>
                  <div className="space-y-0.5">
                    {category.tools.map((tool) => (
                      <button
                        key={tool.id}
                        onClick={() => { setActiveTool(tool.id); setActiveCategory(category.id); setResult(null); setUploadedFile(null); setUploadedFiles([]); }}
                        data-testid={`tool-${tool.id}`}
                        className={`w-full text-left px-3 py-2 rounded-xl transition-all text-sm flex items-center gap-2 ${
                          activeTool === tool.id
                            ? 'bg-[#FAF5FF] text-[#7E22CE] font-semibold'
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                          activeTool === tool.id ? 'bg-[#A855F7] text-white' : 'bg-slate-100 text-slate-500'
                        }`}>{tool.icon}</span>
                        <span>{tool.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card className="p-8 rounded-3xl border-slate-200 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]">
              {currentTool && (
                <>
                  <div className="flex items-center gap-3 mb-6 pb-6 border-b border-slate-100">
                    <div className="w-11 h-11 bg-[#111111] rounded-2xl flex items-center justify-center text-white">
                      {currentTool.icon}
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-[#111111]">{currentTool.name}</h2>
                      <p className="text-sm text-slate-500">{currentTool.category}</p>
                    </div>
                  </div>

                  {renderToolUI()}
                  {renderResult()}
                </>
              )}
            </Card>
          </div>
        </div>

        {/* Related Tools Section */}
        <div className="max-w-7xl mx-auto pt-10">
          <RelatedTools currentToolId={activeTool} category="design" />
        </div>
      </div>
      <Footer />
      <WhatsAppChat />
    </div>
  );
}
